import { Select, Text, Flex, Badge } from '@radix-ui/themes';
import { ListItemType } from '../../types';
import { useGetListsQuery } from '../../graphql/queries/GetLists.generated';

interface ListSelectorProps {
  value: string | undefined;
  onChange: (listId: string) => void;
  /** Filter lists by item type (e.g., ADDRESS for address lists) */
  itemType?: ListItemType;
  placeholder?: string;
}

/**
 * A dropdown selector for choosing a list from the user's available lists.
 * Used with IN_LIST and NOT_IN_LIST condition operators.
 */
const ListSelector = ({
  value,
  onChange,
  itemType,
  placeholder = 'Select a list...',
}: ListSelectorProps) => {
  const { data, loading, error } = useGetListsQuery({
    variables: { first: 100 },
  });

  const lists = data?.lists?.edges?.map((edge) => edge.node) ?? [];

  // Filter by item type if specified
  const filteredLists = itemType
    ? lists.filter((list) => list.itemType === itemType)
    : lists;

  // Separate system lists (like OFAC) from user lists for better UX
  const systemLists = filteredLists.filter((list) => list.isSystem);
  const userLists = filteredLists.filter((list) => !list.isSystem);

  if (loading) {
    return (
      <Select.Root disabled>
        <Select.Trigger placeholder="Loading lists..." />
      </Select.Root>
    );
  }

  if (error) {
    return (
      <Text size="1" color="red">
        Error loading lists
      </Text>
    );
  }

  if (filteredLists.length === 0) {
    return (
      <Flex direction="column" gap="1">
        <Select.Root disabled>
          <Select.Trigger placeholder="No lists available" />
        </Select.Root>
        <Text size="1" color="gray">
          {itemType === ListItemType.Address
            ? 'Create an address list first to use list-based conditions.'
            : 'Create a list first to use list-based conditions.'}
        </Text>
      </Flex>
    );
  }

  return (
    <Select.Root value={value} onValueChange={onChange}>
      <Select.Trigger placeholder={placeholder} style={{ width: '100%' }} />
      <Select.Content>
        {systemLists.length > 0 && (
          <Select.Group>
            <Select.Label>System Lists</Select.Label>
            {systemLists.map((list) => (
              <Select.Item key={list.id} value={list.id}>
                <Flex align="center" gap="2">
                  <Badge size="1" color="red" variant="soft">
                    System
                  </Badge>
                  <Text>{list.name}</Text>
                  <Text size="1" color="gray">
                    ({list.items.totalCount} items)
                  </Text>
                </Flex>
              </Select.Item>
            ))}
          </Select.Group>
        )}
        {userLists.length > 0 && (
          <Select.Group>
            <Select.Label>Your Lists</Select.Label>
            {userLists.map((list) => (
              <Select.Item key={list.id} value={list.id}>
                <Flex align="center" gap="2">
                  <Text>{list.name}</Text>
                  <Text size="1" color="gray">
                    ({list.items.totalCount} items)
                  </Text>
                </Flex>
              </Select.Item>
            ))}
          </Select.Group>
        )}
      </Select.Content>
    </Select.Root>
  );
};

export default ListSelector;
